import type { SessionService } from '@efdigital/ll-addon-auth';
import QuizInput from '@efdigital/ll-addon-content/components/quiz/quiz-input';
import quizValidate from '@efdigital/ll-addon-content/utils/quiz-validate';
import {
  type Answer,
  type Quiz,
  type QuizQuestion,
} from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import OnboardingAnswerSummary from '@efdigital/ll-xceed-v2/components/coaching/onboarding-answer-summary.gts';
import { upsertAnswer } from '@efdigital/ll-xceed-v2/data/builders/quiz/answers';
import { scrollToTop } from '@efdigital/ll-xceed-v2/utils/application/scroll';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface ChemistryQuizSignature {
  Args: {
    quiz: Quiz;
    answer: Answer;
    onSubmitted?: (answer: Answer) => void;
    initialMode: 'input' | 'output';
  };
}

export class ChemistryQuiz extends Component<ChemistryQuizSignature> {
  @service declare session: SessionService;
  @service declare store: Store;

  @tracked submittingAnswer = false;

  submitAnswer = async (answer: Answer) => {
    this.submittingAnswer = true;
    try {
      answer.draft = false;
      await this.store.request(upsertAnswer(answer, this.store));
      this.args.onSubmitted?.(answer);
    } finally {
      this.submittingAnswer = false;
    }
  };

  isValidAnswer = (questions: QuizQuestion[], answer: Answer) => {
    const validationErrors = quizValidate(
      {
        questions,
      },
      answer.values,
      answer.consent
    );

    return validationErrors.length === 0;
  };

  get submitAnswerDisabled() {
    if (this.submittingAnswer) {
      return true;
    }
    return !this.isValidAnswer(this.args.quiz.questions, this.args.answer);
  }

  get isOutputMode() {
    return this.args.initialMode === 'output' && !this.switchedToInputMode;
  }

  @tracked switchedToInputMode = false;

  switchToInputMode = () => {
    this.switchedToInputMode = true;
    scrollToTop();
  };

  get showCancelButton() {
    return this.args.initialMode === 'output' && this.switchedToInputMode;
  }

  revertToOutputMode = () => {
    this.args.answer.rollbackAttributes();
    this.switchedToInputMode = false;
    scrollToTop();
  };

  <template>
    {{#if this.isOutputMode}}
      <Heading
        @size="lg"
        @text="Your Responses"
        @secondary="Coaching onboarding questionnaire"
        @class="mb-4"
      />
      <OnboardingAnswerSummary @quiz={{@quiz}} @answer={{@answer}} />

      <div class="d-flex justify-content-center mt-5">
        <button
          {{on "click" this.switchToInputMode}}
          class="btn btn-primary"
          type="button"
        >Change my answers</button>
      </div>
    {{else}}
      <QuizInput @quiz={{@quiz}} @answers={{@answer}} as |input|>
        {{#if input.buttons}}
          <div class="d-flex justify-content-end gap-2">
            {{#if this.showCancelButton}}
              <button
                {{on "click" this.revertToOutputMode}}
                class="btn btn-outline-primary"
                type="button"
              >Revert changes</button>
            {{/if}}
            <button
              {{on "click" (fn this.submitAnswer @answer)}}
              class="btn btn-primary"
              type="button"
              disabled={{this.submitAnswerDisabled}}
              aria-busy={{this.submittingAnswer}}
            >
              Submit answers
            </button>
          </div>
        {{/if}}
      </QuizInput>
    {{/if}}
  </template>
}
